import {
  CREATE_CREDIT_CARD,
  FETCH_CREDIT_CARDS,
  FETCH_INVOICES,
  REMOVE_CREDIT_CARD,
  SET_PRIMARY_CARD,
  FETCH_PRIMARY_CARD,
  FETCH_SUBSCRIPTION,
  SAVE_PLAID_ACCOUNT,
  FETCH_SUBSCRIPTION_PLANS_INFO,
  FETCH_BANK_ACCOUNTS,
  FETCH_PAYMENT_METHODS
} from "./types";
import * as stripeApi from "@/api-client/stripe";
import * as billingApi from "@/api-client/billing";

import { sortSearchPaginateActions } from "@/store/common/actions";

export default {
  async createCreditCard({ commit }, { data, onSuccess, onError }) {
    commit({
      type: CREATE_CREDIT_CARD.request
    });

    try {
      const stripeResp = await stripeApi.createToken(data);
      const creditCard = await billingApi.createCreditCard({
        stripe_token: stripeResp.id
      });

      commit({
        type: CREATE_CREDIT_CARD.success,
        payload: creditCard
      });

      onSuccess ? onSuccess(creditCard) : null;
      return creditCard;
    } catch (e) {
      const err = e.error || {};

      commit({
        type: CREATE_CREDIT_CARD.error,
        error: e.message
      });

      onError ? onError(err) : null;
    }
  },

  async fetchPaymentMethods({ commit }) {
    commit({
      type: FETCH_PAYMENT_METHODS.request
    });

    try {
      const { fetchBillingInfo, fetchBankAccounts } = billingApi;

      const [creditCards, bankAccounts] = await Promise.all([
        fetchBillingInfo(),
        fetchBankAccounts()
      ]);

      commit({
        type: FETCH_PAYMENT_METHODS.success,
        payload: {
          creditCards,
          bankAccounts
        }
      });
    } catch (e) {
      commit({
        type: FETCH_PAYMENT_METHODS.error,
        payload: e.message
      });
    }
  },

  async fetchCreditCards({ commit }) {
    commit({
      type: FETCH_CREDIT_CARDS.request
    });

    try {
      const creditCards = await billingApi.fetchBillingInfo();

      commit({
        type: FETCH_CREDIT_CARDS.success,
        payload: creditCards
      });
    } catch (e) {
      commit({
        type: FETCH_CREDIT_CARDS.error,
        error: e.message
      });
    }
  },

  async fetchInvoices(
    { commit },
    { page, sort, page_size, search, queryString } = {}
  ) {
    commit({
      type: FETCH_INVOICES.request
    });

    try {
      const invoices = await billingApi.fetchInvoices({
        sort,
        page,
        page_size,
        search,
        queryString
      });

      commit({
        type: FETCH_INVOICES.success,
        payload: invoices
      });
    } catch (e) {
      commit({
        type: FETCH_INVOICES.error,
        error: e.message
      });
    }
  },

  async removeCreditCard({ commit }, { id, onSuccess } = {}) {
    commit({
      type: REMOVE_CREDIT_CARD.request,
      payload: id
    });

    try {
      await billingApi.removeCreditCard(id);

      commit({
        type: REMOVE_CREDIT_CARD.success,
        payload: id
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: REMOVE_CREDIT_CARD.error,
        error: e.message
      });
    }
  },

  async setPrimaryCard({ commit }, { id, onSuccess, onError } = {}) {
    commit({
      type: SET_PRIMARY_CARD.request
    });

    try {
      await billingApi.setPrimaryCard(id);

      commit({
        type: SET_PRIMARY_CARD.success,
        payload: id
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: SET_PRIMARY_CARD.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async fetchPrimaryCard({ commit }) {
    commit({
      type: FETCH_PRIMARY_CARD.request
    });

    try {
      const primary = await billingApi.fetchPrimaryCard();
      const { default_payment_method_id: defaultPmId } = primary;
      let pmDetails;

      if (defaultPmId.includes("card")) {
        pmDetails = await billingApi.fetchCreditCard(defaultPmId);
      } else {
        pmDetails = await billingApi.fetchBankAccount(defaultPmId);
      }

      commit({
        type: FETCH_PRIMARY_CARD.success,
        payload: pmDetails[0]
      });
    } catch (e) {
      commit({
        type: FETCH_PRIMARY_CARD.error,
        error: e.message
      });
    }
  },

  async fetchSubscription({ commit }) {
    commit({
      type: FETCH_SUBSCRIPTION.request
    });

    try {
      const subscription = await billingApi.fecthSubscription();
      commit({
        type: FETCH_SUBSCRIPTION.success,
        payload: subscription
      });
    } catch (e) {
      commit({
        type: FETCH_SUBSCRIPTION.error,
        error: e.message
      });
    }
  },

  async savePlaidAccount(
    { commit },
    { token, account_id, onSuccess, onError } = {}
  ) {
    commit({
      type: SAVE_PLAID_ACCOUNT.request
    });

    try {
      const plaid_account = await billingApi.savePlaid(token, account_id);

      commit({
        type: SAVE_PLAID_ACCOUNT.success,
        payload: plaid_account
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: SAVE_PLAID_ACCOUNT.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async fetchSubscriptionsPlansInfo({ commit }) {
    commit({
      type: FETCH_SUBSCRIPTION_PLANS_INFO.request
    });

    try {
      const subscriptionPlans = await billingApi.fetchSubscriptionsPlansInfo();

      commit({
        type: FETCH_SUBSCRIPTION_PLANS_INFO.success,
        payload: subscriptionPlans.public_plans
      });
    } catch (e) {
      commit({
        type: FETCH_SUBSCRIPTION_PLANS_INFO.error,
        error: e.message
      });
    }
  },

  async fetchBankAccounts({ commit }) {
    commit({
      type: FETCH_BANK_ACCOUNTS.request
    });

    try {
      let bankAccounts = await billingApi.fetchBankAccounts();
      const primary = await billingApi.fetchPrimaryCard();

      if (primary.default_payment_method_id) {
        const idx = bankAccounts.findIndex(
          item => item.id === primary.default_payment_method_id
        );

        const primaryAccount = bankAccounts.splice(idx, 1);

        commit({
          type: FETCH_BANK_ACCOUNTS.success,
          payload: [...primaryAccount, ...bankAccounts]
        });

        return;
      }

      commit({
        type: FETCH_BANK_ACCOUNTS.success,
        payload: bankAccounts
      });
    } catch (e) {
      commit({
        type: FETCH_BANK_ACCOUNTS.error,
        error: e.message
      });
    }
  },

  ...sortSearchPaginateActions
};
